import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Users,
  Zap,
  Download,
  CreditCard,
  Calendar,
  Target,
  BarChart3,
  PieChart,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Image,
  Palette,
  Share2,
  Clock,
  DollarSign,
  UserPlus,
  Eye,
  MousePointer
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';

// Types for launch metrics
interface LaunchMetrics {
  // User Acquisition
  total_signups: number;
  signups_today: number;
  signups_this_week: number;
  signups_this_month: number;
  signup_growth_rate: number;
  
  // User Engagement
  daily_active_users: number;
  weekly_active_users: number;
  monthly_active_users: number;
  average_session_duration: number;
  user_retention_7_day: number;
  user_retention_30_day: number;
  
  // Content Creation
  total_content_generated: number;
  content_generated_today: number;
  content_generated_this_week: number;
  content_generated_this_month: number;
  avg_content_per_user: number;
  
  // AI Usage
  ai_generations_total: number;
  ai_generations_today: number;
  ai_generations_this_week: number;
  ai_generations_this_month: number;
  most_popular_ai_type: string;
  ai_success_rate: number;
  
  // Export/Download
  total_exports: number;
  exports_today: number;
  exports_this_week: number;
  exports_this_month: number;
  most_popular_export_format: string;
  
  // Revenue
  total_revenue: number;
  revenue_today: number;
  revenue_this_week: number;
  revenue_this_month: number;
  mrr: number;
  arr: number;
  
  // Conversion
  free_to_paid_conversion_rate: number;
  trial_to_paid_conversion_rate: number;
  churn_rate: number;
  customer_lifetime_value: number;
  
  // Feature Adoption
  brand_kits_created: number;
  templates_used: number;
  team_invitations_sent: number;
  social_waitlist_signups: number;
  
  // Goals Progress
  monthly_signup_goal: number;
  monthly_revenue_goal: number;
  monthly_content_goal: number;
}

interface LaunchGoals {
  signups_week_1: number;
  signups_month_1: number;
  revenue_month_1: number;
  content_generations_month_1: number;
  paid_conversions_month_1: number;
}

import launchMetricsService from '../../services/launchMetricsService';

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  subtitle?: string;
  color?: string;
}> = ({ title, value, change, icon, trend = 'neutral', subtitle, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    pink: 'from-pink-500 to-pink-600',
    indigo: 'from-indigo-500 to-indigo-600',
  };

  return (
    <Card className="relative overflow-hidden bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200/60 dark:border-slate-700/60 hover:shadow-lg transition-all duration-300">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
              {title}
            </p>
            <div className="flex items-baseline space-x-2">
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
              {change !== undefined && (
                <div className={`flex items-center space-x-1 ${
                  trend === 'up' ? 'text-green-600' : 
                  trend === 'down' ? 'text-red-600' : 
                  'text-slate-500'
                }`}>
                  {trend === 'up' && <ArrowUpRight className="w-4 h-4" />}
                  {trend === 'down' && <ArrowDownRight className="w-4 h-4" />}
                  <span className="text-sm font-medium">
                    {change > 0 ? '+' : ''}{change}%
                  </span>
                </div>
              )}
            </div>
            {subtitle && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {subtitle}
              </p>
            )}
          </div>
          <div className={`w-12 h-12 bg-gradient-to-br ${colorClasses[color]} rounded-xl flex items-center justify-center shadow-lg`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Goal Progress Component
const GoalProgress: React.FC<{
  title: string;
  current: number;
  target: number;
  icon: React.ReactNode;
  color?: string;
  onGoalChange: (newGoal: number) => void;
}> = ({ title, current, target, icon, color = 'blue', onGoalChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newTarget, setNewTarget] = useState(target);

  const progress = Math.min((current / target) * 100, 100);
  const isComplete = current >= target;

  const handleSave = () => {
    onGoalChange(newTarget);
    setIsEditing(false);
  };

  return (
    <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200/60 dark:border-slate-700/60">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-lg flex items-center justify-center`}>
              {icon}
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                {title}
              </h3>
              {isEditing ? (
                <div className="flex items-center space-x-2">
                  <Input
                    type="number"
                    value={newTarget}
                    onChange={(e) => setNewTarget(parseInt(e.target.value))}
                    className="w-24 h-8"
                  />
                  <Button onClick={handleSave} size="sm">Save</Button>
                </div>
              ) : (
                <p className="text-sm text-slate-600 dark:text-slate-400 underline cursor-pointer" onClick={() => setIsEditing(true)}>
                  {current.toLocaleString()} / {target.toLocaleString()}
                </p>
              )}
            </div>
          </div>
          <Badge variant={isComplete ? "default" : "secondary"} className={
            isComplete ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" : ""
          }>
            {progress.toFixed(1)}%
          </Badge>
        </div>
        <Progress value={progress} className="h-2" />
      </CardContent>
    </Card>
  );
};

const LaunchSuccessPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('month');

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['launch-metrics', selectedPeriod],
    queryFn: () => launchMetricsService.getMetrics(selectedPeriod),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: goals, isLoading: isLoadingGoals } = useQuery({
    queryKey: ['launch-goals'],
    queryFn: launchMetricsService.getGoals,
  });

  const [modifiedGoals, setModifiedGoals] = useState<LaunchGoals | undefined>(goals);

  useEffect(() => {
    setModifiedGoals(goals);
  }, [goals]);

  const handleGoalChange = (goal: keyof LaunchGoals, value: number) => {
    setModifiedGoals(prevGoals => {
      if (!prevGoals) return undefined;
      return {
        ...prevGoals,
        [goal]: value,
      };
    });
  };

  if (isLoading || !metrics || isLoadingGoals) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading launch metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-900 dark:via-slate-900 dark:to-gray-900">
      {/* Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <motion.div 
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 via-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black bg-gradient-to-r from-gray-900 via-blue-600 to-purple-600 bg-clip-text text-transparent dark:from-white dark:via-blue-300 dark:to-purple-300">
                  Launch Success Dashboard
                </h1>
                <p className="text-slate-600 dark:text-slate-300">Track your content-creation launch metrics and goals</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant={selectedPeriod === 'today' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedPeriod('today')}
              >
                Today
              </Button>
              <Button
                variant={selectedPeriod === 'week' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedPeriod('week')}
              >
                This Week
              </Button>
              <Button
                variant={selectedPeriod === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedPeriod('month')}
              >
                This Month
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
            <TabsTrigger value="goals">Goals</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Signups"
                value={metrics.total_signups}
                change={metrics.signup_growth_rate}
                trend="up"
                icon={<UserPlus className="w-6 h-6 text-white" />}
                subtitle={`${metrics.signups_this_month} this month`}
                color="blue"
              />
              <MetricCard
                title="Content Generated"
                value={metrics.total_content_generated}
                change={12.5}
                trend="up"
                icon={<Sparkles className="w-6 h-6 text-white" />}
                subtitle={`${metrics.content_generated_this_month} this month`}
                color="purple"
              />
              <MetricCard
                title="Monthly Revenue"
                value={`$${metrics.revenue_this_month.toLocaleString()}`}
                change={23.1}
                trend="up"
                icon={<DollarSign className="w-6 h-6 text-white" />}
                subtitle={`MRR: $${metrics.mrr.toLocaleString()}`}
                color="green"
              />
              <MetricCard
                title="Active Users"
                value={metrics.monthly_active_users}
                change={8.7}
                trend="up"
                icon={<Activity className="w-6 h-6 text-white" />}
                subtitle={`${metrics.daily_active_users} today`}
                color="orange"
              />
            </div>

            {/* Secondary Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetricCard
                title="AI Success Rate"
                value={`${metrics.ai_success_rate}%`}
                change={2.1}
                trend="up"
                icon={<Zap className="w-5 h-5 text-white" />}
                subtitle={`${metrics.ai_generations_this_month} generations`}
                color="indigo"
              />
              <MetricCard
                title="Content Exports"
                value={metrics.total_exports}
                change={15.3}
                trend="up"
                icon={<Download className="w-5 h-5 text-white" />}
                subtitle={`${metrics.exports_this_month} this month`}
                color="pink"
              />
              <MetricCard
                title="Conversion Rate"
                value={`${metrics.free_to_paid_conversion_rate}%`}
                change={-1.2}
                trend="down"
                icon={<Target className="w-5 h-5 text-white" />}
                subtitle="Free to paid"
                color="orange"
              />
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetricCard
                title="Daily Active Users"
                value={metrics.daily_active_users}
                change={5.2}
                trend="up"
                icon={<Users className="w-6 h-6 text-white" />}
                subtitle="Last 24 hours"
                color="blue"
              />
              <MetricCard
                title="7-Day Retention"
                value={`${metrics.user_retention_7_day}%`}
                change={3.1}
                trend="up"
                icon={<Clock className="w-6 h-6 text-white" />}
                subtitle="Users returning after 7 days"
                color="green"
              />
              <MetricCard
                title="Avg Session Duration"
                value={`${metrics.average_session_duration}m`}
                change={12.3}
                trend="up"
                icon={<Eye className="w-6 h-6 text-white" />}
                subtitle="Minutes per session"
                color="purple"
              />
              <MetricCard
                title="Social Waitlist"
                value={metrics.social_waitlist_signups}
                change={45.2}
                trend="up"
                icon={<Share2 className="w-6 h-6 text-white" />}
                subtitle="Users waiting for social features"
                color="orange"
              />
              <MetricCard
                title="Team Invitations"
                value={metrics.team_invitations_sent}
                change={8.7}
                trend="up"
                icon={<UserPlus className="w-6 h-6 text-white" />}
                subtitle="Collaboration adoption"
                color="indigo"
              />
              <MetricCard
                title="Churn Rate"
                value={`${metrics.churn_rate}%`}
                change={-0.5}
                trend="up"
                icon={<TrendingUp className="w-6 h-6 text-white" />}
                subtitle="Monthly churn"
                color="pink"
              />
            </div>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetricCard
                title="AI Generations"
                value={metrics.ai_generations_total}
                change={18.5}
                trend="up"
                icon={<Zap className="w-6 h-6 text-white" />}
                subtitle={`${metrics.ai_generations_this_month} this month`}
                color="purple"
              />
              <MetricCard
                title="Brand Kits Created"
                value={metrics.brand_kits_created}
                change={25.3}
                trend="up"
                icon={<Palette className="w-6 h-6 text-white" />}
                subtitle="Brand consistency adoption"
                color="pink"
              />
              <MetricCard
                title="Templates Used"
                value={metrics.templates_used}
                change={12.1}
                trend="up"
                icon={<Image className="w-6 h-6 text-white" />}
                subtitle="Template engagement"
                color="indigo"
              />
              <MetricCard
                title="Avg Content/User"
                value={metrics.avg_content_per_user.toFixed(1)}
                change={7.8}
                trend="up"
                icon={<BarChart3 className="w-6 h-6 text-white" />}
                subtitle="Content per user"
                color="blue"
              />
              <MetricCard
                title="Popular AI Type"
                value={metrics.most_popular_ai_type}
                icon={<Sparkles className="w-6 h-6 text-white" />}
                subtitle="Most used AI feature"
                color="green"
              />
              <MetricCard
                title="Export Format"
                value={metrics.most_popular_export_format}
                icon={<Download className="w-6 h-6 text-white" />}
                subtitle="Most popular format"
                color="orange"
              />
            </div>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetricCard
                title="Monthly Recurring Revenue"
                value={`$${metrics.mrr.toLocaleString()}`}
                change={23.1}
                trend="up"
                icon={<DollarSign className="w-6 h-6 text-white" />}
                subtitle="Current MRR"
                color="green"
              />
              <MetricCard
                title="Annual Run Rate"
                value={`$${metrics.arr.toLocaleString()}`}
                change={23.1}
                trend="up"
                icon={<TrendingUp className="w-6 h-6 text-white" />}
                subtitle="Projected ARR"
                color="blue"
              />
              <MetricCard
                title="Customer LTV"
                value={`$${metrics.customer_lifetime_value.toFixed(2)}`}
                change={15.7}
                trend="up"
                icon={<Target className="w-6 h-6 text-white" />}
                subtitle="Lifetime value"
                color="purple"
              />
              <MetricCard
                title="Revenue Today"
                value={`$${metrics.revenue_today.toFixed(2)}`}
                change={12.3}
                trend="up"
                icon={<Calendar className="w-6 h-6 text-white" />}
                subtitle="Today's revenue"
                color="orange"
              />
              <MetricCard
                title="Free to Paid Rate"
                value={`${metrics.free_to_paid_conversion_rate}%`}
                change={-1.2}
                trend="down"
                icon={<CreditCard className="w-6 h-6 text-white" />}
                subtitle="Conversion rate"
                color="indigo"
              />
              <MetricCard
                title="Trial Conversion"
                value={`${metrics.trial_to_paid_conversion_rate}%`}
                change={5.8}
                trend="up"
                icon={<MousePointer className="w-6 h-6 text-white" />}
                subtitle="Trial to paid"
                color="pink"
              />
            </div>
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals" className="space-y-6">
            {modifiedGoals && (
              <>
                {/* Overall Goal Completion Summary */}
                <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200/60 dark:border-slate-700/60">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Launch Goals Overview
                      </h3>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                        Month 1 Targets
                      </Badge>
                    </div>
                    
                    {/* Overall Progress */}
                    <div className="mb-6">
                      {(() => {
                        const signupProgress = Math.min((metrics.signups_this_month / modifiedGoals.signups_month_1) * 100, 100);
                        const revenueProgress = Math.min((metrics.revenue_this_month / modifiedGoals.revenue_month_1) * 100, 100);
                        const contentProgress = Math.min((metrics.content_generated_this_month / modifiedGoals.content_generations_month_1) * 100, 100);
                        const conversionProgress = Math.min((Math.floor(metrics.total_signups * (metrics.free_to_paid_conversion_rate / 100)) / modifiedGoals.paid_conversions_month_1) * 100, 100);
                        const overallProgress = (signupProgress + revenueProgress + contentProgress + conversionProgress) / 4;
                        
                        return (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                Overall Goal Completion
                              </span>
                              <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                {overallProgress.toFixed(1)}%
                              </span>
                            </div>
                            <Progress value={overallProgress} className="h-3" />
                          </div>
                        );
                      })()}
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {Math.min((metrics.signups_this_month / modifiedGoals.signups_month_1) * 100, 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">Signups</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {Math.min((metrics.revenue_this_month / modifiedGoals.revenue_month_1) * 100, 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">Revenue</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {Math.min((metrics.content_generated_this_month / modifiedGoals.content_generations_month_1) * 100, 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">Content</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                          {Math.min((Math.floor(metrics.total_signups * (metrics.free_to_paid_conversion_rate / 100)) / modifiedGoals.paid_conversions_month_1) * 100, 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400">Conversions</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Individual Goal Progress */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <GoalProgress
                    title="Monthly Signups Goal"
                    current={metrics.signups_this_month}
                    target={modifiedGoals.signups_month_1}
                    icon={<UserPlus className="w-5 h-5 text-white" />}
                    color="blue"
                    onGoalChange={(newGoal) => handleGoalChange('signups_month_1', newGoal)}
                  />
                  <GoalProgress
                    title="Monthly Revenue Goal"
                    current={metrics.revenue_this_month}
                    target={modifiedGoals.revenue_month_1}
                    icon={<DollarSign className="w-5 h-5 text-white" />}
                    color="green"
                    onGoalChange={(newGoal) => handleGoalChange('revenue_month_1', newGoal)}
                  />
                  <GoalProgress
                    title="Content Generation Goal"
                    current={metrics.content_generated_this_month}
                    target={modifiedGoals.content_generations_month_1}
                    icon={<Sparkles className="w-5 h-5 text-white" />}
                    color="purple"
                    onGoalChange={(newGoal) => handleGoalChange('content_generations_month_1', newGoal)}
                  />
                  <GoalProgress
                    title="Paid Conversions Goal"
                    current={Math.floor(metrics.total_signups * (metrics.free_to_paid_conversion_rate / 100))}
                    target={modifiedGoals.paid_conversions_month_1}
                    icon={<Target className="w-5 h-5 text-white" />}
                    color="orange"
                    onGoalChange={(newGoal) => handleGoalChange('paid_conversions_month_1', newGoal)}
                  />
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LaunchSuccessPage;